import { useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import OfferPageStateContext from 'contexts/OfferPage/offerPageStateContext'
import Clickable from 'components/Common/Clickable'
import Group from 'components/utils/Group'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import BodyText from 'LuxKit/Typography/BodyText'
import TextButton from 'LuxKit/Button/TextButton'
import LinePlaneDepartureIcon from 'LuxKit/Icons/line/LinePlaneDepartureIcon'
import LineAngleRightBIcon from 'LuxKit/Icons/line/LineAngleRightBIcon'
import { toBaseFareClass } from 'constants/flight'
import useSamePropertyCharterOffer from 'hooks/CharterFlights/useSamePropertyCharterOffer'
import useCharterBundleRecommendation from 'hooks/CharterFlights/useCharterBundleRecommendation'
import { getCharterBundleRecommendationUrl } from 'lib/charterFlights/charterBundleRecommendation'

const Banner = styled(Clickable)`
  display: flex;
  align-items: center;
  gap: ${rem(12)};
  width: 100%;
  padding: ${rem(16)};
  border: ${rem(1)} solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.M};
  background: ${props => props.theme.palette.messaging.success.lightBackground};
  color: ${props => props.theme.palette.neutral.surface.normalContrast};
  text-align: start;
`

const Media = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: ${rem(40)};
  height: ${rem(40)};
  border-radius: ${props => props.theme.borderRadius.round};
  background: ${props => props.theme.palette.neutral.surface.normalBackground};
`

interface Props { offer: App.HotelOffer }

export default function MaldivesBundleCrossSellBanner({ offer }: Props) {
  const intl = useIntl()
  const { rooms, duration, checkInDate, checkOutDate, fareClass, airport } = useContext(OfferPageStateContext)
  const { charterOffer, fetching } = useSamePropertyCharterOffer(offer)
  const { recommendation, fareClass: recommendationCabin } = useCharterBundleRecommendation({
    offer: charterOffer,
    rooms,
    duration,
    fareClass: toBaseFareClass(fareClass),
    checkIn: checkInDate,
    checkOut: checkOutDate,
    disabled: !charterOffer || fetching,
  })
  const url = useMemo(() => charterOffer && recommendation && recommendationCabin ? getCharterBundleRecommendationUrl(
    charterOffer, recommendation, rooms, recommendationCabin, airport?.code, !!checkInDate && !!checkOutDate,
  ) : undefined, [charterOffer, recommendation, recommendationCabin, rooms, airport?.code, checkInDate, checkOutDate])
  const heading = intl.formatMessage({
    id: 'eegRn74uOrL46JL1qtYs',
    defaultMessage: 'Bundle this hotel with direct flights to the Maldives',
    description: 'Headline of the mapped-hotel charter bundle cross-sell above the hotel duration selector',
  })
  const description = intl.formatMessage({
    id: 'DgzDiuvp84ma5g-8zDq5',
    defaultMessage: 'Spend more time in paradise with Australia’s only direct flight to Malé.',
    description: 'Supporting copy in the hotel offer cross-sell promoting direct Melbourne to Maldives charter flights',
  })
  const cta = intl.formatMessage({
    id: '2wokHFRY9C8tzpzFLAqW',
    defaultMessage: 'View direct flight bundles',
    description: 'Call to action opening the same hotel charter bundle offer in a new browser tab',
  })
  const accessibleLabel = intl.formatMessage({
    id: 'lsYAiq3jN3Et-ndu8GFD',
    defaultMessage: 'View direct flight bundles (opens in a new tab)',
    description: 'Accessible label for the hotel offer charter bundle cross-sell opening a new browser tab',
  })
  if (!charterOffer || fetching || !url) return undefined
  return <Banner to={url} target="_blank" rel="noopener noreferrer" aria-label={accessibleLabel} data-testid="maldives-bundle-cross-sell-banner">
    <Media aria-hidden="true"><LinePlaneDepartureIcon size={20} /></Media>
    <Group direction="vertical" gap={4} grow shrink={1}>
      <BodyText variant="medium" weight="bold">{heading}</BodyText>
      <BodyText variant="medium">{description}</BodyText>
    </Group>
    <CSSBreakpoint min="tablet"><TextButton nonInteractable kind="secondary" size="medium" shape="round">{cta}</TextButton></CSSBreakpoint>
    <CSSBreakpoint max="mobile"><LineAngleRightBIcon size={20} aria-hidden="true" /></CSSBreakpoint>
  </Banner>
}
